"use client";

import { Trans } from "@lingui/react/macro";
import { AlertCircleIcon, XIcon } from "lucide-react";
import { type ReactNode, useMemo } from "react";
import { Button } from "@/components/ui/button";
import {
  type ChatExternalErrorState,
  resolveRecommendedModelForChatExternalError,
} from "@/lib/ai/chat-external-error";
import type { AiModelAccess } from "@/lib/ai/model-access";
import type { ModelId } from "@/lib/ai/models";
import { cn } from "@/lib/utils";

type Props = {
  error: ChatExternalErrorState | null;
  currentModel: ModelId;
  aiModelAccess: Pick<AiModelAccess, "restrictedModelIds">;
  onModelChange: (model: ModelId) => void;
  onStartNewChat: () => void;
  onDismiss: () => void;
  className?: string;
};

export function ChatExternalErrorCard({
  error,
  currentModel,
  aiModelAccess,
  onModelChange,
  onStartNewChat,
  onDismiss,
  className,
}: Props) {
  const recommendedModel = useMemo(
    () =>
      error
        ? resolveRecommendedModelForChatExternalError({
            error,
            currentModel,
            aiModelAccess,
          })
        : null,
    [error, currentModel, aiModelAccess],
  );

  if (!error) {
    return null;
  }

  let title: ReactNode;
  let description: ReactNode;
  let toneClassName = "border-border bg-background text-foreground";
  let iconClassName = "text-foreground/70";

  if (error.kind === "context-limit") {
    iconClassName = "text-amber-600/80";
    title = <Trans>Conversation limit reached</Trans>;
    description = (
      <Trans>
        The conversation is too long for this model. Start a new chat or use a
        model with a larger context window.
      </Trans>
    );
  } else if (error.kind === "overloaded") {
    iconClassName = "text-muted-foreground";
    title = <Trans>Model temporarily busy</Trans>;
    description = (
      <Trans>
        This model is temporarily overloaded. Please switch to another model and
        try again.
      </Trans>
    );
  } else if (error.kind === "refusal") {
    title = <Trans>Model declined to respond</Trans>;
    description = (
      <Trans>
        The model declined this request. Review and rephrase your message.
      </Trans>
    );
  } else if (error.kind === "network") {
    title = <Trans>Couldn't connect</Trans>;
    description = (
      <Trans>Check your network connection and send the message again.</Trans>
    );
  } else {
    iconClassName = "text-foreground/70";
    title = <Trans>Message couldn&apos;t be sent</Trans>;
    description = <Trans>An error occurred while sending the message.</Trans>;
  }

  return (
    <div
      className={cn(
        "flex flex-col gap-2 rounded-2xl border px-3 py-2.5 shadow-none",
        toneClassName,
        className,
      )}
    >
      <div className="flex items-start gap-2">
        <div className="mt-0.5 shrink-0">
          <AlertCircleIcon className={cn("h-4 w-4", iconClassName)} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <div className="text-sm leading-5 font-medium">{title}</div>
              <div className="mt-0.5 text-sm leading-5 text-muted-foreground">
                {description}
              </div>
            </div>
            <Button
              variant="ghost"
              size="icon"
              className="-mr-1 -mt-1 size-6 shrink-0 text-muted-foreground"
              onClick={onDismiss}
            >
              <XIcon className="h-4 w-4" />
              <span className="sr-only">
                <Trans>Dismiss</Trans>
              </span>
            </Button>
          </div>
        </div>
      </div>
      <div className="pl-6">
        <div className="flex flex-wrap items-center gap-1.5">
          {recommendedModel ? (
            <Button
              size="sm"
              variant="outline"
              className="h-7 rounded-lg border-border px-2.5 text-[11px] shadow-none"
              onClick={() => {
                onDismiss();
                onModelChange(recommendedModel.value);
              }}
            >
              <Trans>Switch to {recommendedModel.label}</Trans>
            </Button>
          ) : null}
          {error.kind !== "overloaded" &&
          error.kind !== "network" &&
          error.kind !== "refusal" ? (
            <Button
              size="sm"
              variant="outline"
              className="h-7 rounded-lg border-border px-2.5 text-[11px] shadow-none"
              onClick={() => {
                onDismiss();
                onStartNewChat();
              }}
            >
              <Trans>Start a new chat</Trans>
            </Button>
          ) : null}
        </div>
      </div>
    </div>
  );
}
